import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import {
  AdminRole,
  AdminUser,
  adminUsersApi,
} from '../../../api/adminUsersApi';
import { TenantGroup, tenantGroupsApi } from '../../../api/tenantGroupsApi';
import { colors, tabsSx, typo } from '../../../theme/tokens';

import UsersTable from './components/UsersTable';
import RolesTable from './components/RolesTable';
import GroupsTable from './components/GroupsTable';

type TabKey = 'users' | 'roles' | 'groups';

/**
 * UsersAdminPage — RBAC_V2 (design §9) renamed this to UsersAdminPageLegacy:
 * the flag-off page, rendered by the UsersAdminPage flag switch. Content is
 * BYTE-IDENTICAL to the pre-flag page (Users / Roles / Tenant Groups,
 * CPT-1132/1133) — only the file name + exported component name changed.
 */
const UsersAdminPageLegacy: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initial: TabKey =
    tabParam === 'roles' ? 'roles' : tabParam === 'groups' ? 'groups' : 'users';
  const [tab, setTab] = useState<TabKey>(initial);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });

  const usersQuery = useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: () => adminUsersApi.listUsers(),
  });
  const rolesQuery = useQuery<AdminRole[]>({
    queryKey: ['admin', 'roles'],
    queryFn: () => adminUsersApi.listRoles(),
  });
  const groupsQuery = useQuery<TenantGroup[]>({
    queryKey: ['tenant-groups'],
    queryFn: () => tenantGroupsApi.listGroups(),
  });

  const handleTab = (next: TabKey) => {
    setTab(next);
    const nsp = new URLSearchParams(searchParams);
    if (next === 'users') nsp.delete('tab');
    else nsp.set('tab', next);
    setSearchParams(nsp, { replace: true });
  };

  const notify = useMemo(
    () => (msg: string, sev: 'success' | 'error' | 'info') =>
      setSnackbar({ open: true, msg, sev }),
    [],
  );

  const loading = usersQuery.isLoading || rolesQuery.isLoading;
  const error = usersQuery.isError || rolesQuery.isError;
  const users = usersQuery.data ?? [];
  const roles = rolesQuery.data ?? [];
  const groups = groupsQuery.data ?? [];

  return (
    <Box sx={{ maxWidth: 1200 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: colors.textTertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            mb: 0.5,
          }}
        >
          Admin
        </Typography>
        <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>Users</Typography>
        <Typography sx={{ ...typo.pageSubtitle }}>
          Manage platform access. Invites land immediately; Auth0 identity binds on the
          user&apos;s first login.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => handleTab(v)}
        sx={tabsSx}
        aria-label="Users, Roles, Tenant Groups"
      >
        <Tab value="users" label={`Users (${users.length})`} />
        <Tab value="roles" label={`Roles (${roles.length})`} />
        <Tab value="groups" label={`Tenant Groups (${groups.length})`} />
      </Tabs>

      {/* Tenant Groups is self-contained (own query + loading/error state) and
          is dispatched before the shared users/roles fetch gate below. */}
      {tab === 'groups' ? (
        <GroupsTable
          groups={groups}
          loading={groupsQuery.isLoading}
          error={groupsQuery.isError}
          onNotify={notify}
        />
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: colors.adminAccent }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: '8px' }}>
          Failed to load users / roles — try again.
        </Alert>
      ) : tab === 'users' ? (
        <UsersTable users={users} roles={roles} onNotify={notify} />
      ) : (
        <RolesTable roles={roles} onNotify={notify} />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersAdminPageLegacy;
